/**
 * Mission Control summary helpers for Order detail (Sprint 8.6).
 * Thin wrappers — do not fork health/milestone logic.
 */

import { getMilestoneView } from "./orderMilestoneView.js";

/**
 * Pick a short place label from address components or address string.
 * @param {Object|null|undefined} location
 * @returns {string}
 */
export function getPlaceLabel(location) {
  if (!location || typeof location !== "object") return "—";
  const components = location.addressComponents || {};
  const city =
    components.city ||
    components.locality ||
    components.town ||
    components.suburb ||
    null;
  if (city) return String(city);
  const address = location.address || location.formattedAddress || "";
  if (!address) return "—";
  const first = String(address).split(",")[0]?.trim();
  return first || String(address);
}

/**
 * @param {Object} order
 * @returns {{ origin: string, destination: string }}
 */
export function getCorridorLabel(order) {
  return {
    origin: getPlaceLabel(order?.pickup),
    destination: getPlaceLabel(order?.delivery),
  };
}

/**
 * @param {Object} order
 * @returns {{ partnerName: string|null, driverName: string|null }}
 */
export function getAssignedOwner(order) {
  const team = order?.booking?.assignedTeam || order?.assignedTeam || null;
  const partnerName = team?.partnerName || null;
  const driverName =
    team?.primaryDriver?.name ||
    team?.drivers?.[0]?.name ||
    null;
  return { partnerName, driverName };
}

/**
 * Prefer drop deliveryProgress % when present; else stage index progress.
 * Label clarifies this is stage/drop progress, not GPS.
 *
 * @param {Object|null} order
 * @param {Object|null} [trip]
 * @returns {{ percent: number|null, label: string, mode: "drops"|"stages"|"none" }}
 */
export function getStageProgress(order, trip = null) {
  const dropPct = Number(order?.deliveryProgress?.progressPercentage);
  if (
    Number.isFinite(dropPct) &&
    Array.isArray(order?.deliveryDrops) &&
    order.deliveryDrops.length > 0
  ) {
    return {
      percent: Math.max(0, Math.min(100, Math.round(dropPct))),
      label: "Drop progress",
      mode: "drops",
    };
  }

  const view = getMilestoneView(order, trip);
  const stages = view?.stages || [];
  if (stages.length === 0) {
    return { percent: null, label: "Stage progress", mode: "none" };
  }

  const currentIndex = stages.findIndex((s) => s.state === "current");
  const completedCount = stages.filter((s) => s.state === "completed").length;
  const index = currentIndex >= 0 ? currentIndex : completedCount;
  const percent = Math.round(((index + (currentIndex >= 0 ? 0.5 : 0)) / stages.length) * 100);

  return {
    percent: Math.max(0, Math.min(100, percent)),
    label: "Stage progress",
    mode: "stages",
  };
}

/**
 * @param {Object} order
 * @returns {{ lat: number, lng: number }|null}
 */
export function getCoords(location) {
  const lat = Number(location?.coordinates?.lat ?? location?.lat);
  const lng = Number(location?.coordinates?.lng ?? location?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * @param {Object} order
 * @returns {{ pickup: {lat:number,lng:number}|null, delivery: {lat:number,lng:number}|null, hasRoute: boolean }}
 */
export function getRouteCoordinates(order) {
  const pickup = getCoords(order?.pickup);
  const delivery = getCoords(order?.delivery);
  return {
    pickup,
    delivery,
    hasRoute: Boolean(pickup && delivery),
  };
}
